/**
 * Export Excel (.xlsx) du tableau de bord de pilotage.
 *
 * Combine deux sources :
 *  - les statistiques de pilotage déjà chargées par le dashboard
 *    (`/api/admin/stats/*` : overview / courriers / departs / affectations) ;
 *  - les statistiques de connexion (mêmes données que la page « Statistiques de
 *    connexion »), reconstruites à la demande depuis les journaux d'audit.
 *
 * L'utilisateur choisit librement les jeux de données à inclure ; chacun
 * devient une feuille du classeur. SheetJS est utilisé en écriture seule.
 */
import {
  fetchAuditLogs, fetchUserMap, filterLogs, loginsPerDay, dayRange,
  loginsPerHour, loginsPerWeekday, distinctUsers, todayStats, buildSessions,
  avgSessionMs, totalSessionMs, topUsers, loginsPerAmbassade, formatDuration,
} from './loginStats'
import type { AuditLog, UserMap } from './loginStats'

type AOA = (string | number | null)[][]

// ───────────────────────── Libellés FR (statuts) ─────────────────────────
const NOTE_STATUS_LABELS: Record<string, string> = {
  draft: 'Brouillon', brouillon: 'Brouillon', en_validation: 'En validation',
  en_attente_validation: 'En attente de validation', validee: 'Validée',
  valide: 'Validée', rejetee: 'Rejetée', finalisee: 'Finalisée',
  en_attente_archivage: "En attente d'archivage", archive: 'Archivée', archived: 'Archivée',
}
const AFFECTATION_STATUS_LABELS: Record<string, string> = {
  masque: 'Masquée', en_attente: 'En attente', accepte: 'Acceptée',
  en_rédaction: 'En rédaction', en_traitement: 'En traitement',
  en_consultation: 'En consultation', traite: 'Traitée', rejete: 'Rejetée',
  transferee: 'Transférée',
}
const lbl = (map: Record<string, string>, s: string) =>
  map[(s || '').toLowerCase().trim()] || s || '—'

// ───────────────────────── Agrégats connexions ─────────────────────────
export interface LoginAggregates {
  kpi: { todayUsers: number; todayTotal: number; totalLogins: number; activeUsers: number; avgSession: string; totalUsage: string }
  perDay: Array<[string, number]>
  perHour: number[]
  perWeekday: number[]
  perAmbassade: Array<[string, number]>
  top: Array<{ name: string; ambassade: string | null; count: number }>
  activity: { notesCreated: number; notesSubmitted: number; notesValidees: number; affCreated: number; affTraitees: number } | null
}

export interface ExportFilters {
  fromISO: string
  toISO: string
  ambassadeId?: number | null
  directionId?: number | null
  roleCode?: string | null
  userId?: number | null
}

/** Reconstruit les agrégats de connexion sur la fenêtre + périmètre donnés. */
export async function fetchLoginAggregates(
  f: ExportFilters,
  withActivity: boolean,
): Promise<LoginAggregates> {
  const userMap: UserMap = await fetchUserMap()
  const opts = {
    ambassadeId: f.ambassadeId ?? null,
    directionId: f.directionId ?? null,
    roleCode: f.roleCode ?? null,
    userId: f.userId ?? null,
  }
  const [rawIn, rawOut] = await Promise.all([
    fetchAuditLogs('auth.login_success', f.fromISO, f.toISO),
    fetchAuditLogs('auth.logout', f.fromISO, f.toISO),
  ])
  const logins = filterLogs(rawIn, userMap, opts)
  const logouts = filterLogs(rawOut, userMap, opts)
  const sessions = buildSessions(logins, logouts)
  const today = todayStats(logins)

  const perDayMap = loginsPerDay(logins)
  const keys = dayRange(new Date(f.fromISO), new Date(f.toISO))
  const perDay: Array<[string, number]> = keys.map((k) => [k, perDayMap[k] || 0])
  const perAmbassade = Object.entries(loginsPerAmbassade(logins, userMap)).sort((a, b) => b[1] - a[1])
  const top = topUsers(logins, userMap, 20)

  let activity: LoginAggregates['activity'] = null
  if (withActivity) {
    const [nc, ns, val, ac, at] = await Promise.all([
      fetchAuditLogs('note.creee', f.fromISO, f.toISO),
      fetchAuditLogs('note.soumise', f.fromISO, f.toISO),
      fetchAuditLogs('validation.validee', f.fromISO, f.toISO),
      fetchAuditLogs('affectation.creee', f.fromISO, f.toISO),
      fetchAuditLogs('affectation.traitee', f.fromISO, f.toISO),
    ])
    const cnt = (logs: AuditLog[]) => filterLogs(logs, userMap, opts).length
    activity = {
      notesCreated: cnt(nc), notesSubmitted: cnt(ns), notesValidees: cnt(val),
      affCreated: cnt(ac), affTraitees: cnt(at),
    }
  }

  return {
    kpi: {
      todayUsers: today.users,
      todayTotal: today.total,
      totalLogins: logins.length,
      activeUsers: distinctUsers(logins),
      avgSession: formatDuration(avgSessionMs(sessions)),
      totalUsage: formatDuration(totalSessionMs(sessions)),
    },
    perDay, perHour: loginsPerHour(logins), perWeekday: loginsPerWeekday(logins),
    perAmbassade, top, activity,
  }
}

// ───────────────────────── Catalogue des feuilles ─────────────────────────
export interface ExportCtx {
  overview: any
  courriers: any
  departs: any
  affectations: any
  login: LoginAggregates | null
}

export interface ExportItem {
  id: string
  label: string
  group: string
  default: boolean
  /** Nécessite les agrégats de connexion. */
  needsLogin?: boolean
  /** Nécessite en plus l'activité plateforme (notes/affectations audités). */
  needsActivity?: boolean
  sheet: string
  build: (ctx: ExportCtx) => AOA | null
}

const num = (v: any) => (v == null ? 0 : v)
const WEEKDAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']

export const EXPORT_CATALOG: ExportItem[] = [
  // ───── Synthèse ─────
  {
    id: 'kpi_global', group: 'Synthèse', label: 'KPI globaux', default: true, sheet: 'KPI globaux',
    build: (c) => {
      const o = c.overview
      if (!o) return null
      return [
        ['Indicateur', 'Valeur'],
        ['Documents (total)', num(o.documents.total)],
        ['Courriers arrivés', num(o.documents.arrives_total)],
        ['Courriers départ', num(o.documents.departs_total)],
        ['Affectations actives', num(o.affectations.total)],
        ['Transferts', num(o.affectations.transferts)],
        ['Archivés', num(o.archives?.total)],
      ]
    },
  },
  {
    id: 'progression', group: 'Synthèse', label: 'Progression des courriers (état courant)', default: true, sheet: 'Progression courriers',
    build: (c) => {
      const rows = c.overview?.par_etape || []
      if (!rows.length) return null
      return [['Étape', 'Nombre'], ...rows.map((r: any) => [r.label, num(r.count)])]
    },
  },
  {
    id: 'archives_recherche', group: 'Synthèse', label: 'Archives & santé recherche', default: false, sheet: 'Archives & recherche',
    build: (c) => {
      const o = c.overview
      if (!o) return null
      return [
        ['Indicateur', 'Valeur'],
        ['Documents archivés (total)', num(o.archives?.total)],
        ['Courriers archivés', num(o.archives?.courriers)],
        ['Notes archivées', num(o.archives?.notes)],
        ['Recherches', num(o.recherche?.total)],
        ['Latence moyenne (ms)', num(o.recherche?.latence_moyenne_ms)],
        ['Sans résultat (%)', num(o.recherche?.sans_resultat_pct)],
        ['Recherches IA', num(o.recherche?.ai)],
      ]
    },
  },
  // ───── Courriers arrivés ─────
  {
    id: 'courriers_importateurs', group: 'Courriers arrivés', label: 'Qui a importé', default: true, sheet: 'Importateurs',
    build: (c) => {
      const rows = c.courriers?.par_importateur || []
      if (!rows.length) return null
      return [['Agent', 'Identifiant', 'Imports'], ...rows.map((r: any) => [r.full_name || r.username || '—', r.username || '', num(r.count)])]
    },
  },
  {
    id: 'courriers_ambassade', group: 'Courriers arrivés', label: 'Par ambassade', default: false, sheet: 'Arrivés par ambassade',
    build: (c) => {
      const rows = c.courriers?.par_ambassade || []
      if (!rows.length) return null
      return [['Ambassade', 'Imports'], ...rows.map((r: any) => [r.label || '—', num(r.count)])]
    },
  },
  {
    id: 'courriers_direction', group: 'Courriers arrivés', label: 'Par direction', default: false, sheet: 'Arrivés par direction',
    build: (c) => {
      const rows = c.courriers?.par_direction || []
      if (!rows.length) return null
      return [['Direction', 'Imports'], ...rows.map((r: any) => [r.label || '—', num(r.count)])]
    },
  },
  {
    id: 'courriers_envois', group: 'Courriers arrivés', label: 'Envois (SG / Ambassadeur)', default: false, sheet: 'Envois SG-Ambassadeur',
    build: (c) => {
      const co = c.courriers
      if (!co) return null
      const out: AOA = [['Destination', 'Agent', 'Envois']]
      out.push(['— Envoyés au SG —', '', num(co.envoyes_sg?.total)])
      for (const r of co.envoyes_sg?.par_acteur || []) out.push(['SG', r.full_name || r.username || '—', num(r.count)])
      out.push(["— Envoyés à l'ambassadeur —", '', num(co.envoyes_ambassadeur?.total)])
      for (const r of co.envoyes_ambassadeur?.par_acteur || []) out.push(['Ambassadeur', r.full_name || r.username || '—', num(r.count)])
      return out.length > 1 ? out : null
    },
  },
  // ───── Courriers départ ─────
  {
    id: 'departs_statut', group: 'Courriers départ', label: 'Par statut', default: true, sheet: 'Départs par statut',
    build: (c) => {
      const rows = c.departs?.par_statut || []
      if (!rows.length) return null
      return [['Statut', 'Nombre'], ...rows.map((r: any) => [lbl(NOTE_STATUS_LABELS, r.statut), num(r.count)])]
    },
  },
  {
    id: 'departs_type', group: 'Courriers départ', label: 'Par type de document', default: false, sheet: 'Départs par type',
    build: (c) => {
      const rows = c.departs?.par_type || []
      if (!rows.length) return null
      return [['Type de document', 'Nombre'], ...rows.map((r: any) => [r.type || '—', num(r.count)])]
    },
  },
  // ───── Affectations ─────
  {
    id: 'affectations_statut', group: 'Affectations', label: 'Par statut (actives)', default: true, sheet: 'Affectations par statut',
    build: (c) => {
      const rows = c.affectations?.par_statut || []
      if (!rows.length) return null
      return [['Statut', 'Nombre'], ...rows.map((r: any) => [lbl(AFFECTATION_STATUS_LABELS, r.statut), num(r.count)])]
    },
  },
  {
    id: 'affectations_direction', group: 'Affectations', label: 'Par direction', default: false, sheet: 'Affectations direction',
    build: (c) => {
      const rows = c.affectations?.par_direction || []
      if (!rows.length) return null
      return [['Direction', 'Nombre'], ...rows.map((r: any) => [r.label || '—', num(r.count)])]
    },
  },
  // ───── Connexions ─────
  {
    id: 'conn_kpi', group: 'Connexions', label: 'KPI connexions', default: true, needsLogin: true, sheet: 'KPI connexions',
    build: (c) => {
      const k = c.login?.kpi
      if (!k) return null
      return [
        ['Indicateur', 'Valeur'],
        ["Connectés aujourd'hui", k.todayUsers],
        ["Connexions aujourd'hui", k.todayTotal],
        ['Connexions sur la période', k.totalLogins],
        ['Utilisateurs actifs distincts', k.activeUsers],
        ['Durée moyenne de session', k.avgSession],
        ['Durée cumulée', k.totalUsage],
      ]
    },
  },
  {
    id: 'conn_par_jour', group: 'Connexions', label: 'Connexions par jour', default: true, needsLogin: true, sheet: 'Connexions par jour',
    build: (c) => {
      const rows = c.login?.perDay || []
      if (!rows.length) return null
      return [['Jour', 'Connexions'], ...rows.map(([d, n]) => [d, n])]
    },
  },
  {
    id: 'conn_top_users', group: 'Connexions', label: 'Utilisateurs les plus actifs', default: true, needsLogin: true, sheet: 'Top utilisateurs',
    build: (c) => {
      const rows = c.login?.top || []
      if (!rows.length) return null
      return [['Utilisateur', 'Rattachement', 'Connexions'], ...rows.map((u) => [u.name, u.ambassade || '—', u.count])]
    },
  },
  {
    id: 'conn_ambassade', group: 'Connexions', label: 'Connexions par ambassade', default: false, needsLogin: true, sheet: 'Connexions ambassade',
    build: (c) => {
      const rows = c.login?.perAmbassade || []
      if (!rows.length) return null
      return [['Ambassade / Direction', 'Connexions'], ...rows.map(([l, n]) => [l, n])]
    },
  },
  {
    id: 'conn_horaire', group: 'Connexions', label: 'Pics horaires', default: false, needsLogin: true, sheet: 'Pics horaires',
    build: (c) => {
      const h = c.login?.perHour
      if (!h) return null
      return [['Heure', 'Connexions'], ...h.map((n, i) => [`${String(i).padStart(2, '0')}h`, n])]
    },
  },
  {
    id: 'conn_jour_semaine', group: 'Connexions', label: 'Par jour de semaine', default: false, needsLogin: true, sheet: 'Jour de semaine',
    build: (c) => {
      const w = c.login?.perWeekday
      if (!w) return null
      return [['Jour', 'Connexions'], ...w.map((n, i) => [WEEKDAYS[i], n])]
    },
  },
  {
    id: 'conn_activite', group: 'Connexions', label: 'Activité plateforme (notes / affectations)', default: false, needsLogin: true, needsActivity: true, sheet: 'Activité plateforme',
    build: (c) => {
      const a = c.login?.activity
      if (!a) return null
      return [
        ['Événement', 'Volume'],
        ['Notes créées', a.notesCreated],
        ['Notes soumises', a.notesSubmitted],
        ['Notes validées', a.notesValidees],
        ['Affectations créées', a.affCreated],
        ['Affectations traitées', a.affTraitees],
      ]
    },
  },
]

// ───────────────────────── Génération du classeur ─────────────────────────
function autoWidth(aoa: AOA): { wch: number }[] {
  const widths: number[] = []
  for (const row of aoa) {
    row.forEach((cell, i) => {
      const len = String(cell ?? '').length
      widths[i] = Math.max(widths[i] || 10, Math.min(len + 2, 50))
    })
  }
  return widths.map((wch) => ({ wch }))
}

export interface ExportMeta {
  generatedAt: string
  periodLabel: string
  filtersLabel: string
}

/** Construit et télécharge le classeur xlsx pour les datasets sélectionnés.
 *  SheetJS est chargé en dynamic import pour ne pas alourdir le bundle initial. */
export async function buildAndDownload(selectedIds: string[], ctx: ExportCtx, meta: ExportMeta): Promise<number> {
  const mod: any = await import('xlsx')
  const XLSX = mod.default || mod
  const wb = XLSX.utils.book_new()

  // Feuille de garde
  const cover: AOA = [
    ['Tableau de bord eCable — Export'],
    [],
    ['Généré le', meta.generatedAt],
    ['Période', meta.periodLabel],
    ['Filtres', meta.filtersLabel || 'Aucun'],
  ]
  const coverWs = XLSX.utils.aoa_to_sheet(cover)
  coverWs['!cols'] = [{ wch: 16 }, { wch: 50 }]
  XLSX.utils.book_append_sheet(wb, coverWs, 'Synthèse')

  const usedNames = new Set<string>(['Synthèse'])
  let sheetCount = 0
  for (const item of EXPORT_CATALOG) {
    if (!selectedIds.includes(item.id)) continue
    const aoa = item.build(ctx)
    if (!aoa || aoa.length <= 1) continue
    // Nom de feuille unique, ≤ 31 car., sans caractères interdits
    let name = item.sheet.replace(/[[\]:*?/\\]/g, ' ').slice(0, 31)
    let n = 2
    while (usedNames.has(name)) name = `${item.sheet.slice(0, 28)} ${n++}`
    usedNames.add(name)
    const ws = XLSX.utils.aoa_to_sheet(aoa)
    ws['!cols'] = autoWidth(aoa)
    XLSX.utils.book_append_sheet(wb, ws, name)
    sheetCount++
  }

  const stamp = new Date().toISOString().slice(0, 10)
  XLSX.writeFile(wb, `dashboard-ecable-${stamp}.xlsx`)
  return sheetCount
}
